import { getAnswer, getQuestion } from './question/helper/json-helper';
import { getPreselected } from './question/helper/component-helper';
import {
    BM_FATCA_IKKE_TIN,
    BM_FATCA_V2_IKKE_TIN,
    BM_FATCA_TIN,
    BM_FATCA_V2_TIN,
    BM_FINANSIELL_INSTUTISJON_GIN,
    BM_FINANSIELL_INSTUTISJON_IKKE_GIN_ARSAK,
    BM_TYPE_KONTOHAVER,
    PM_FATCA_IKKE_TIN,
    PM_FATCA_V2_IKKE_TIN,
    PM_FATCA_TIN,
    PM_FATCA_V2_TIN,
} from './constants/definisjoner';
import {
    SPORSMALSTYPE_AKTOR_CAP,
    SPORSMALSTYPE_AKTOR_LIST_CAP,
    SPORSMALSTYPE_AMOUNT_CAP,
    SPORSMALSTYPE_CHECKBOX_CAP,
    SPORSMALSTYPE_CRS_CAP,
    SPORSMALSTYPE_CRS_V2_CAP,
    SPORSMALSTYPE_FATCA_V2_CAP,
    SPORSMALSTYPE_INTERNASJONALE_OVERFORINGER_CAP,
    SPORSMALSTYPE_LAND_CAP,
    SPORSMALSTYPE_NUMBER_CAP,
    SPORSMALSTYPE_RADIO_CAP,
    SPORSMALSTYPE_RADIO_CLASSIC_CAP,
    SPORSMALSTYPE_RR_CAP,
    SPORSMALSTYPE_SELECT_CAP,
    SPORSMALSTYPE_TEXT_AREA_CAP,
    SPORSMALSTYPE_DATE_CAP,
    SPORSMALSTYPE_TEXT_CAP,
    SPORSMALSTYPE_TEXT_LIST_CAP,
    SPORSMALSTYPE_DISPONENT_LIST_CAP,
    KUN_LESING,
    BARE_LES_NAR_VALGT,
    SPORSMALSTYPE_CRS_V2_DETAILS_CAP,
    SPORSMALSTYPE_FATCA_V2_DETAILS_CAP,
    ROLLE_RAADGIVER,
} from './constants/general';
import { isSubQuestionAndNotTriggerExistsInAnswers } from './validator';
import { emptyList } from './constants';

const ARRAY_TYPE_QUESTION = [
    SPORSMALSTYPE_CHECKBOX_CAP,
    SPORSMALSTYPE_LAND_CAP,
    SPORSMALSTYPE_RADIO_CAP,
    SPORSMALSTYPE_RADIO_CLASSIC_CAP,
    SPORSMALSTYPE_SELECT_CAP,
    SPORSMALSTYPE_TEXT_LIST_CAP,
];
const SINGLE_VALUE_QUESTION = [
    SPORSMALSTYPE_NUMBER_CAP,
    SPORSMALSTYPE_AMOUNT_CAP,
    SPORSMALSTYPE_TEXT_CAP,
    SPORSMALSTYPE_TEXT_AREA_CAP,
    SPORSMALSTYPE_DATE_CAP,
];

export function getCountryOptionsList(allCountries, excludeList, excludeKey) {
    if (!excludeList || allCountries === emptyList) {
        return emptyList;
    }
    return [
        ...allCountries
            .filter((entry) => !excludeList[excludeKey].includes(entry.landIso))
            .map((entry) => ({
                value: entry.landIso,
                text: entry.landNavn,
            })),
    ];
}

export function isFatcaCrsV2(question) {
    return question.definisjon === SPORSMALSTYPE_FATCA_V2_CAP || question.definisjon === SPORSMALSTYPE_CRS_V2_CAP;
}

export function isFatcaCrsDetailsV2(question) {
    return (
        question.definisjon === SPORSMALSTYPE_FATCA_V2_DETAILS_CAP ||
        question.definisjon === SPORSMALSTYPE_CRS_V2_DETAILS_CAP
    );
}

export const getIsDisabled = (regel, hasPreselectedValue, contextRolle) =>
    regel === KUN_LESING || (regel === BARE_LES_NAR_VALGT && hasPreselectedValue && contextRolle !== ROLLE_RAADGIVER);

export const isDisabledFatcaCrs = (question) => isFatcaCrsV2(question) && getPreselected(question);

function copyOf(anyObject) {
    return JSON.parse(JSON.stringify(anyObject));
}

function findQuestionId(definition, siblingQuestions) {
    if (!siblingQuestions) {
        return [];
    }
    return siblingQuestions.filter((question) => question.definisjon === definition);
}

function findValidateIfNotSetRule(siblingQuestions, question) {
    switch (question.definisjon) {
        case PM_FATCA_TIN:
            return findQuestionId(PM_FATCA_IKKE_TIN, siblingQuestions);
        case PM_FATCA_IKKE_TIN:
            return findQuestionId(PM_FATCA_TIN, siblingQuestions);
        case BM_FATCA_TIN:
            return findQuestionId(BM_FATCA_IKKE_TIN, siblingQuestions);
        case BM_FATCA_IKKE_TIN:
            return findQuestionId(BM_FATCA_TIN, siblingQuestions);
        case PM_FATCA_V2_TIN:
            return findQuestionId(PM_FATCA_V2_IKKE_TIN, siblingQuestions);
        case PM_FATCA_V2_IKKE_TIN:
            return findQuestionId(PM_FATCA_V2_TIN, siblingQuestions);
        case BM_FATCA_V2_TIN:
            return findQuestionId(BM_FATCA_V2_IKKE_TIN, siblingQuestions);
        case BM_FATCA_V2_IKKE_TIN:
            return findQuestionId(BM_FATCA_V2_TIN, siblingQuestions);
        case BM_FINANSIELL_INSTUTISJON_GIN:
            return findQuestionId(BM_FINANSIELL_INSTUTISJON_IKKE_GIN_ARSAK, siblingQuestions);
        case BM_FINANSIELL_INSTUTISJON_IKKE_GIN_ARSAK: {
            return findQuestionId(BM_FINANSIELL_INSTUTISJON_GIN, siblingQuestions);
        }
        default:
            return [];
    }
}

function createSubQuestion(definisjon) {
    return (muligSvar) =>
        muligSvar.undersporsmal.map((undersporsmal) => ({
            ...undersporsmal,
            validateIfNotSet: findValidateIfNotSetRule(muligSvar.undersporsmal, undersporsmal),
            rootQuestion: definisjon,
            isSubQuestion: true,
            requiredParent: muligSvar.svarId,
        }));
}

function flatmapUndersporsmal(sporsmal) {
    return (sporsmal.muligeSvar || [])
        .filter((ms) => ms.undersporsmal)
        .map(createSubQuestion(sporsmal.definisjon))
        .reduce((acc, lst) => [...acc, ...lst], []);
}

/**
 * Hent ut en liste med alle spørsmålsobjekter(inkl. underspørsmål) fra skjema-data
 * @param skjema
 * @returns {Array}
 */
export function getQuestionList(skjema) {
    if (!skjema.skjema || !skjema.skjema.skjemaSporsmal) {
        return [];
    }

    return skjema.skjema.skjemaSporsmal
        .map((sporsmal) => {
            const question = { ...sporsmal };
            // TODO: Litt usikker hvorfor denne er slik?
            if (sporsmal.definisjon === BM_TYPE_KONTOHAVER) {
                question.type = SPORSMALSTYPE_CHECKBOX_CAP;
            }

            const undersporsmal = flatmapUndersporsmal(sporsmal);
            return [question, ...undersporsmal];
        })
        .reduce((acc, lst) => [...acc, ...lst], []);
}

function filterOutValidAnswers(answers, renderedQuestions) {
    return renderedQuestions
        .filter((question) => {
            return !(isSubQuestionAndNotTriggerExistsInAnswers(question, answers) || !answers[question.definisjon]);
        })
        .map((question) => {
            const validAnswer = answers[question.definisjon];
            return [question.definisjon, validAnswer];
        })
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

const normalizeAktorValues = (aktor) => ({
    ...aktor,
    aktorId: {
        value: aktor.fnr || '',
    },
    fodselsdato: undefined,
    fodeland: aktor.fodeland ? aktor.fodeland : null,
    statsborgerskap: aktor.statsborgerskap ? aktor.statsborgerskap : null,
    bostedsland: aktor.bostedsland ? aktor.bostedsland : null,
    sekundartStatsborgerskap: aktor.sekundartStatsborgerskap ? aktor.sekundartStatsborgerskap : null,
});

export function updateSkjemaWithAnswers(kdkForm, answers, renderedQuestions) {
    const form = copyOf(kdkForm);
    const validAnswers = filterOutValidAnswers(answers, renderedQuestions);

    Object.entries(validAnswers).forEach(([definisjon, answer]) => {
        const question = getQuestion(definisjon, form);

        if (question.type === SPORSMALSTYPE_CRS_CAP) {
            const crsAnswers = answer.value.map(
                ({ manglerSkattenummer = '', land = '', skattenummer = '', arsak = '' }) => {
                    return manglerSkattenummer
                        ? { manglerSkattenummer, land, skattenummer: '', skattenummerMangler: arsak }
                        : { manglerSkattenummer, land, skattenummer, skattenummerMangler: '' };
                }
            );
            question.svartekst = JSON.stringify(crsAnswers);
        } else if (question.type === SPORSMALSTYPE_CRS_V2_CAP) {
            const crsAnswers = answer.value.map(
                ({
                    country = '',
                    taxNumber = '',
                    taxNumberMissing = '',
                    fromDate = '',
                    taxRelationEnded = '',
                    taxRelationComment = '',
                }) => {
                    return taxNumberMissing
                        ? { country, taxNumber, taxNumberMissing, fromDate, taxRelationEnded, taxRelationComment }
                        : { country, taxNumber, taxNumberMissing: '', fromDate, taxRelationEnded, taxRelationComment };
                }
            );
            question.svartekst = JSON.stringify(crsAnswers);
        } else if (question.type === SPORSMALSTYPE_FATCA_V2_CAP) {
            const fatcaAnswers = answer.value.map(
                ({
                    country = 'USA',
                    taxNumber = '',
                    taxNumberMissing = '',
                    fromDate = '',
                    taxRelationEnded = '',
                    taxRelationComment = '',
                }) => {
                    return { country, taxNumber, taxNumberMissing, fromDate, taxRelationEnded, taxRelationComment };
                }
            );
            question.svartekst = JSON.stringify(fatcaAnswers);
        } else if (question.type === SPORSMALSTYPE_RR_CAP) {
            // Clean up values the server never wants to receive
            const rrList = answer.value.map((entry) => ({
                ...normalizeAktorValues(entry),
                skatterelasjoner: entry.skatterelasjonerFlere ? entry.skatterelasjoner : undefined,
                eierandelProsent: parseFloat(`${entry.eierandelProsent}`),
            }));
            question.svartekst = JSON.stringify(rrList);
        } else if (question.type === SPORSMALSTYPE_AKTOR_CAP) {
            const aktor = normalizeAktorValues(answer.value);
            question.svartekst = JSON.stringify(aktor);
        } else if (
            question.type === SPORSMALSTYPE_AKTOR_LIST_CAP ||
            question.type === SPORSMALSTYPE_DISPONENT_LIST_CAP
        ) {
            const aktorList = answer.value.map((aktor) => normalizeAktorValues(aktor));
            question.svartekst = JSON.stringify(aktorList);
        } else if (question.type === SPORSMALSTYPE_INTERNASJONALE_OVERFORINGER_CAP) {
            const intOverforinger = answer.value.map(
                ({ hvorfor = '', land = '', belop = '', antOverforinger = '', type = '' }) => ({
                    hvorfor,
                    land,
                    belop,
                    antOverforinger,
                    type,
                })
            );
            question.svartekst = JSON.stringify(intOverforinger);
        } else if (ARRAY_TYPE_QUESTION.includes(question.type)) {
            question.muligeSvar.forEach((s) => {
                s.valgt = false;
            });
            answer.value.forEach((svarId) => {
                getAnswer(svarId, question).valgt = true;
            });
        } else if (SINGLE_VALUE_QUESTION.includes(question.type)) {
            question.svartekst = answer.value;
        } else {
            // We should never reach this
            // eslint-disable-next-line no-console
            console.debug('OBS, noe gikk galt!', question.type);
        }
    });
    return form.skjema.skjemaSporsmal;
}

export function prefillFatcaCrsV2(currentValues) {
    const jsonValue = JSON.parse(currentValues);
    return jsonValue.map((value, index) => ({
        id: `${value.country === 'USA' ? 'FATCA' : 'CRS'}-${index}`,
        country: value.country,
        taxNumber: value.taxNumber,
        taxNumberMissing: value.taxNumberMissing,
        showTaxNumberMissing: !value.taxNumber || value.taxNumber === '' || value.taxNumberMissing,
        fromDate: value.fromDate,
        taxRelationEnded: undefined,
        taxRelationValid: undefined,
        taxRelationComment: undefined,
        error: false,
        touched: false,
        prefilled: true,
    }));
}

function hasPossibleAnswersAndSelected(question) {
    return (
        question &&
        question.muligeSvar &&
        question.muligeSvar.length > 0 &&
        question.muligeSvar.some((svar) => svar.valgt)
    );
}

function hasSvartekst(question) {
    return !!question.svartekst;
}

function hasAnswer(question) {
    return hasPossibleAnswersAndSelected(question) || hasSvartekst(question);
}

function toAnswer(question) {
    const svartekst =
        question.svartekst && isFatcaCrsDetailsV2(question)
            ? prefillFatcaCrsV2(question.svartekst)
            : question.svartekst;
    return [
        question.definisjon,
        {
            value: svartekst || question.muligeSvar.filter((svar) => svar.valgt).map((svar) => svar.svarId),
            error: false,
        },
    ];
}

export function prefillValuesWithAnswersFromQuestionList(questionList) {
    return questionList
        .filter(hasAnswer)
        .map(toAnswer)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
}

export function newFatcaCrsV2(currentValues, isFatca) {
    return [
        ...currentValues,
        {
            id: `${isFatca ? 'FATCA' : 'CRS'}-${currentValues.length}`,
            country: isFatca ? 'USA' : '',
            taxNumber: '',
            taxNumberMissing: '',
            showTaxNumberMissing: '',
            fromDate: new Date().toISOString().slice(0, 10),
            taxRelationEnded: false,
            taxRelationValid: false,
            taxRelationComment: '',
            error: false,
            touched: false,
            prefilled: false,
        },
    ];
}
